import React, { ChangeEvent, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import UploadingCard from './UploadingCard';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { SetUploadStatus, SetUploadedFile, SetUploadedUrl } from '../../store/translate.page.slice';

import { MAX_FILE_UPLOAD_SIZE } from '../../utils/env';
import { EUploadStatus } from '../../@types/Upload';

function TranslateUploader() {
    const dispatch = useAppDispatch();
    const status = useAppSelector((state) => state.pageTranslate.uploader.status);
    
    const [uploadFile, setUploadFile] = useState<File|null>(null);

    const onFileDrop = useCallback((acceptedFiles: Array<File>) => {
        if(acceptedFiles.length <= 0) return;
        onAddFile(acceptedFiles[0]);
    }, [])
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: onFileDrop });

    const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        if(!event.target?.files || event.target.files.length <= 0) return;
        let file = event.target.files[0];

        onAddFile(file);
    }

    const onAddFile = (file: File) => {
        if(file.size > MAX_FILE_UPLOAD_SIZE) {
            alert(`Max file size is ${Math.round(MAX_FILE_UPLOAD_SIZE / 1000000)} MB`);
            return;
        }
        
        dispatch(SetUploadStatus(EUploadStatus.Verifying));
        setUploadFile(file);
    }

    const onFileUploadCompleted = useCallback(async (url: string) => {
        if(!uploadFile) {
            onFileRemove();
            return;
        }
        dispatch(SetUploadedUrl(url));
        dispatch(SetUploadedFile({ name: uploadFile?.name, size: uploadFile?.size  }));
        dispatch(SetUploadStatus(EUploadStatus.Uploaded));
    }, [uploadFile]);

    const onFileRemove = useCallback(async () => {
        dispatch(SetUploadStatus(EUploadStatus.NoFileSelected));
        dispatch(SetUploadedUrl(''))
        setUploadFile(null);
    }, []);

    return (
        <div className="flex items-center justify-center w-full mb-10" { ...getRootProps() }>
            { status == EUploadStatus.NoFileSelected && 
            <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                    </svg>
                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">{ isDragActive ? <>Drop here!</> : <><span className="font-semibold">Click to upload</span> or drag and drop</> }</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">SRT (MAX: 5 MB)</p>
                </div>
                <input onChange={onFileChange} id="dropzone-file" type="file" className="hidden" { ...getInputProps() }/>
            </label> 
            }
            { ([EUploadStatus.Verifying, EUploadStatus.Uploading, EUploadStatus.Uploaded].indexOf(status) !== -1 && uploadFile != null) &&
            <UploadingCard file={uploadFile} onFileUploadCompleted={onFileUploadCompleted} onFileRemove={onFileRemove}/>
            }
        </div>
    )
}

export default React.memo(TranslateUploader);